import { m as motion, useScroll } from 'framer-motion';
import { GetStaticProps, InferGetStaticPropsType } from 'next';
import { useEffect, useRef } from 'react';
import type { Thing } from 'schema-dts';
import { PAGES } from '@/consts';
import { Page } from '@/entities/page';
import { AppCompaniesResponse, getCommonPageProps, getCompanies, getCompanyCategories } from '@/shared/api';
import { getPageDetail } from '@/shared/api/page-detail';
import { getQuestions } from '@/shared/api/questions';
import { getReasons } from '@/shared/api/reasons';
import { getSiteSettings } from '@/shared/api/site-settings';
import { useHeaderThemeValue } from '@/shared/atoms/header-theme';
import DefaultLayout from '@/shared/components/layout/DefaultLayout';
import FaqSection, { FaqSectionProps } from '@/shared/components/sections/FaqSection';
import OrganizeSection, { OrganizeSectionProps } from '@/shared/components/sections/OrganizeSection';
import Breadcrumbs from '@/shared/components/shared/Breadcrumbs';
import DocumentLink from '@/shared/components/shared/DocumentLink';
import JsonLd from '@/shared/components/shared/JsonLd/JsonLd';
import NavigationAnchors from '@/shared/components/shared/NavigationAnchors';
import NavigationSections from '@/shared/components/shared/NavigationSections';
import FullscreenSlider, { FullscreenSliderProps } from '@/shared/components/sliders/FullscreenSlider';
import { PARTICIPATION_NAVIGATION } from '@/shared/consts/participation-nav';
import { Anchors, AppNavigation, CommonPageProps, NonUndefined } from '@/shared/lib/types';
import { filterFaqQuestionsVisibleInFaqSection } from '@/shared/lib/utils/faq-json-ld';
import { generateWebPageJsonLd } from '@/shared/lib/utils/page-json-ld';
import { scrollingPageToAnchor } from '@/shared/lib/utils/scrollingPageToAnchor';

const PARTNERS_SECTION_IDS = {
    cause: 'cause',
    organize: 'organize',
    faq: 'faq',
} as const;

const ParticipationPage = ({
    faqSectionProps,
    fullscreenSliderProps,
    page,
    h1,
    meta,
    organizeSectionProps,
    breadcrumbs,
    navigation,
    anchors,
    sectionIds,
    bodyClass,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
    const ref = useRef(null);
    const theme = useHeaderThemeValue();
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start -30%', 'end end'] });
    const tnfPresentationPDF = '/docs/TNF2025_participants_formats.pdf';

    useEffect(() => {
        scrollingPageToAnchor();
    }, []);

    return (
        <DefaultLayout currentPageId={PAGES.PARTNERS} ref={ref} bodyClass={bodyClass}>
            <div className="participation-top js-header-theme-trigger wrapper" data-theme="dark" id="top">
                <Breadcrumbs className="participation-breadcrumbs" breadcrumbs={breadcrumbs} />
                <div className="participation-title-block">
                    <h1 className="visually-hidden">{h1}</h1>
                    <div className="participation-title h2">Варианты участия</div>
                    {page?.file && <DocumentLink document={page?.file} />}
                </div>
                <NavigationSections navigation={navigation} />
            </div>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1 }}
                exit={{ opacity: 0 }}
            >
                <NavigationAnchors
                    theme={theme}
                    navigation={navigation}
                    anchors={anchors}
                    scrollYProgress={scrollYProgress}
                />
                <FullscreenSlider id={sectionIds.cause} fullscreenSliderProps={fullscreenSliderProps} />
                <OrganizeSection id={sectionIds.organize} organizeSectionProps={organizeSectionProps} />
                {filterFaqQuestionsVisibleInFaqSection(faqSectionProps.questions).length > 0 && (
                    <FaqSection id={sectionIds.faq} faqSectionProps={faqSectionProps} />
                )}
            </motion.div>
            <JsonLd<Thing>
                inBody
                scriptKey="ld-json-partners"
                data={generateWebPageJsonLd({
                    name: h1,
                    description: meta.description,
                    pagePath: '/participation/partners/',
                    breadcrumbs,
                    faqQuestions: faqSectionProps.questions,
                })}
            />
        </DefaultLayout>
    );
};

export default ParticipationPage;

type ParticipationPageProps = NonUndefined<
    CommonPageProps & {
        page?: Page;
        h1: string;
        faqSectionProps: FaqSectionProps;
        organizeSectionProps: OrganizeSectionProps;
        fullscreenSliderProps: FullscreenSliderProps;
        navigation: AppNavigation;
        anchors: Anchors;
        sectionIds: typeof PARTNERS_SECTION_IDS;
    }
>;

export const getStaticProps: GetStaticProps<ParticipationPageProps> = async () => {
    const pageResponse = await getPageDetail({ params: { slug: 'partners' } });

    const companyCategoriesResponse = await getCompanyCategories({
        params: { page: pageResponse.data?.id ?? PAGES.PARTNERS },
    });
    const companyCategories = companyCategoriesResponse.data ?? [];

    const [commonPageProps, companiesResponse, questionsResponse, reasonsResponse, siteSettings] = await Promise.all([
        getCommonPageProps(pageResponse.data?.id ?? PAGES.PARTNERS),
        companyCategories.length === 0
            ? Promise.resolve<AppCompaniesResponse>({
                  status: 'success',
                  data: [],
              })
            : getCompanies({ params: { categories: companyCategories.map((category) => category.id) } }),
        getQuestions({ params: { page: pageResponse.data?.id ?? PAGES.PARTNERS } }),
        getReasons({ params: { page: pageResponse.data?.id ?? PAGES.PARTNERS } }),
        getSiteSettings({
            params: {
                keys: ['partners_company_title', 'partners_faq_title'],
            },
        }),
    ]);

    const partnersAnchors: Anchors = [
        ...((reasonsResponse.data ?? []).length > 0
            ? [
                  {
                      id: PARTNERS_SECTION_IDS.cause,
                      name: 'Причины посетить',
                  },
              ]
            : []),
        ...(companyCategories.length > 0
            ? [
                  {
                      id: PARTNERS_SECTION_IDS.organize,
                      name: 'Организаторы и партнеры',
                  },
              ]
            : []),
        ...((questionsResponse.data ?? []).length > 0
            ? [
                  {
                      id: PARTNERS_SECTION_IDS.faq,
                      name: 'Частые вопросы',
                  },
              ]
            : []),
    ];

    return {
        props: {
            ...commonPageProps,
            bodyClass: 'participation-page',
            meta: {
                ...commonPageProps.meta,
                ...(pageResponse.data?.meta ?? []),
            },
            breadcrumbs: [
                { text: 'TNF', href: '/' },
                { text: 'Партнеры', href: '/participation/partners/' },
            ],
            page: pageResponse.data,
            h1: pageResponse.data?.name ?? 'Партнеры',
            navigation: PARTICIPATION_NAVIGATION,
            anchors: partnersAnchors,
            sectionIds: PARTNERS_SECTION_IDS,
            fullscreenSliderProps: {
                slides: reasonsResponse.data ?? [],
            },
            faqSectionProps: {
                title: siteSettings.data?.partners_faq_title ?? 'Частые вопросы',
                questions: questionsResponse.data ?? [],
            },
            organizeSectionProps: {
                title: siteSettings.data?.partners_company_title ?? 'Организаторы и партнеры',
                categories: (companyCategoriesResponse.data ?? []).map((category) => ({
                    title: category.name,
                    description: category.description ?? '',
                    items: (companiesResponse.data ?? []).filter((company) => company.categoryId === category.id),
                })),
            },
        } satisfies ParticipationPageProps,
        revalidate: 60,
    };
};
